import * as XLSX from "xlsx";
import { APP_NAME } from "../constants/app";
import { REPORT_HEADERS, formatAmount } from "./budgetWorkspace";

const PDF_PAGE_WIDTH = 842;
const PDF_PAGE_HEIGHT = 595;
const PDF_FONT_SIZE = 9;
const PDF_LINE_HEIGHT = 16;
const PDF_TOP_MARGIN = 38;
const PDF_LEFT_MARGIN = 26;
const PDF_RIGHT_MARGIN = 26;
const PDF_BOTTOM_MARGIN = 34;
const PDF_TABLE_WIDTH =
  PDF_PAGE_WIDTH - PDF_LEFT_MARGIN - PDF_RIGHT_MARGIN;
const PDF_DESCRIPTION_WIDTH = 360;
const PDF_VALUE_WIDTH = Math.floor(
  (PDF_TABLE_WIDTH - PDF_DESCRIPTION_WIDTH) / 3,
);
const PDF_MAX_DESCRIPTION_CHARS = 46;
const PDF_MAX_VALUE_CHARS = 16;

const escapePdfText = (value = "") => {
  return `${value}`
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
};

const buildHeaderLabel = (header = {}) => {
  return [header?.title, header?.year].filter(Boolean).join(" ");
};

const truncateCellText = (value = "", maxLength = 0) => {
  const normalizedValue = `${value ?? ""}`.trim();
  if (normalizedValue.length <= maxLength) {
    return normalizedValue;
  }

  if (maxLength <= 1) {
    return normalizedValue.slice(0, maxLength);
  }

  return `${normalizedValue.slice(0, maxLength - 1)}…`;
};

const padCellText = (value = "", width = 0, align = "left") => {
  const nextValue = truncateCellText(value, width);
  return align === "right"
    ? nextValue.padStart(width, " ")
    : nextValue.padEnd(width, " ");
};

const createDownloadLink = (blob, fileName) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};

const buildExportSections = ({ sections = [], plannerHeaderLabels = {} }) => {
  return sections.flatMap((section) => {
    const rows = Array.isArray(section?.rows) ? section.rows : [];

    return [
      [section?.name || REPORT_HEADERS.executiveSummary],
      [
        "VI Code",
        "Line Item",
        buildHeaderLabel(plannerHeaderLabels.previousBudget),
        buildHeaderLabel(plannerHeaderLabels.actualOutcome),
        buildHeaderLabel(plannerHeaderLabels.estimated),
      ],
      ...rows.map((row) => [
        row.viCode ?? "",
        row.title,
        formatAmount(row.originalBudget),
        formatAmount(row.actualOutcome),
        formatAmount(row.estimatedBudget),
      ]),
      [],
    ];
  });
};

export const downloadBudgetExcel = ({
  financialYear,
  plannerHeaderLabels,
  sections,
}) => {
  const workbook = XLSX.utils.book_new();
  const exportRows = [
    [APP_NAME],
    [`Budget Planner ${financialYear}`],
    [],
    ...buildExportSections({ sections, plannerHeaderLabels }),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(exportRows);
  worksheet["!cols"] = [
    { wch: 12 },
    { wch: 42 },
    { wch: 22 },
    { wch: 22 },
    { wch: 22 },
  ];
  XLSX.utils.book_append_sheet(workbook, worksheet, "Budget Planner");
  XLSX.writeFile(workbook, `${APP_NAME}-Budget-${financialYear}.xlsx`);
};

const buildPdfLines = ({ financialYear, plannerHeaderLabels, sections = [] }) => {
  const headerCells = [
    padCellText("Line Item", PDF_MAX_DESCRIPTION_CHARS, "left"),
    padCellText(
      buildHeaderLabel(plannerHeaderLabels.previousBudget),
      PDF_MAX_VALUE_CHARS,
      "right",
    ),
    padCellText(
      buildHeaderLabel(plannerHeaderLabels.actualOutcome),
      PDF_MAX_VALUE_CHARS,
      "right",
    ),
    padCellText(
      buildHeaderLabel(plannerHeaderLabels.estimated),
      PDF_MAX_VALUE_CHARS,
      "right",
    ),
  ];

  return sections.flatMap((section) => {
    const rows = Array.isArray(section?.rows) ? section.rows : [];

    return [
      {
        type: "sectionTitle",
        text: section?.name || REPORT_HEADERS.executiveSummary,
      },
      { type: "tableTopRule" },
      {
        type: "tableHeader",
        cells: headerCells,
      },
      { type: "tableHeaderBottomRule" },
      ...rows.map((row) => ({
        type: "tableRow",
        cells: [
          padCellText(row.title, PDF_MAX_DESCRIPTION_CHARS, "left"),
          padCellText(
            formatAmount(row.originalBudget),
            PDF_MAX_VALUE_CHARS,
            "right",
          ),
          padCellText(
            formatAmount(row.actualOutcome),
            PDF_MAX_VALUE_CHARS,
            "right",
          ),
          padCellText(
            formatAmount(row.estimatedBudget),
            PDF_MAX_VALUE_CHARS,
            "right",
          ),
        ],
      })),
      { type: "tableBottomRule" },
      { type: "spacer" },
    ];
  });
};

export const downloadBudgetPdf = ({
  financialYear,
  plannerHeaderLabels,
  sections,
}) => {
  const lineWidth = PDF_PAGE_WIDTH - PDF_LEFT_MARGIN - PDF_RIGHT_MARGIN;
  const sectionsContent = buildPdfLines({
    financialYear,
    plannerHeaderLabels,
    sections,
  });
  const pages = [];
  let currentOperations = [];
  let currentY = PDF_PAGE_HEIGHT - PDF_TOP_MARGIN;

  const flushPage = () => {
    if (currentOperations.length === 0) {
      return;
    }

    pages.push(currentOperations.join("\n"));
    currentOperations = [];
    currentY = PDF_PAGE_HEIGHT - PDF_TOP_MARGIN;
  };

  const ensureSpace = (heightNeeded) => {
    if (currentY - heightNeeded < PDF_BOTTOM_MARGIN) {
      flushPage();
    }
  };

  const drawTextLine = (text, options = {}) => {
    const x = options.x ?? PDF_LEFT_MARGIN;
    currentOperations.push(
      `BT /F1 ${PDF_FONT_SIZE} Tf 1 0 0 1 ${x} ${currentY} Tm (${escapePdfText(
        text,
      )}) Tj ET`,
    );
    currentY -= options.height ?? PDF_LINE_HEIGHT;
  };

  const drawHorizontalRule = () => {
    currentOperations.push(
      `${PDF_LEFT_MARGIN} ${currentY} m ${PDF_LEFT_MARGIN + lineWidth} ${currentY} l S`,
    );
    currentY -= 10;
  };

  ensureSpace(PDF_LINE_HEIGHT * 2);
  drawTextLine(APP_NAME);
  drawTextLine(`Budget Planner ${financialYear}`);
  currentY -= 8;

  sectionsContent.forEach((item) => {
    if (item.type === "sectionTitle") {
      ensureSpace(PDF_LINE_HEIGHT + 12);
      drawTextLine(item.text);
      return;
    }

    if (item.type === "tableTopRule") {
      ensureSpace(12);
      drawHorizontalRule();
      return;
    }

    if (item.type === "tableHeader") {
      ensureSpace(PDF_LINE_HEIGHT);
      const headerText = `${item.cells[0]} ${item.cells[1]} ${item.cells[2]} ${item.cells[3]}`;
      drawTextLine(headerText);
      return;
    }

    if (item.type === "tableHeaderBottomRule") {
      ensureSpace(12);
      drawHorizontalRule();
      return;
    }

    if (item.type === "tableRow") {
      ensureSpace(PDF_LINE_HEIGHT);
      const rowText = `${item.cells[0]} ${item.cells[1]} ${item.cells[2]} ${item.cells[3]}`;
      drawTextLine(rowText);
      return;
    }

    if (item.type === "tableBottomRule") {
      ensureSpace(12);
      drawHorizontalRule();
      return;
    }

    if (item.type === "spacer") {
      currentY -= 6;
    }
  });

  flushPage();

  const objects = [];
  const addObject = (value) => {
    objects.push(value);
    return objects.length;
  };

  const fontObjectId = addObject(
    "<< /Type /Font /Subtype /Type1 /BaseFont /Courier >>",
  );
  const pageObjectIds = [];

  pages.forEach((pageCommands) => {
    const contentObjectId = addObject(
      `<< /Length ${pageCommands.length} >>\nstream\n${pageCommands}\nendstream`,
    );
    const pageObjectId = addObject(
      `<< /Type /Page /Parent PAGES_ID 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}] /Contents ${contentObjectId} 0 R /Resources << /Font << /F1 ${fontObjectId} 0 R >> >> >>`,
    );
    pageObjectIds.push(pageObjectId);
  });

  const pagesObjectId = addObject(
    `<< /Type /Pages /Count ${pageObjectIds.length} /Kids [${pageObjectIds
      .map((id) => `${id} 0 R`)
      .join(" ")}] >>`,
  );
  const catalogObjectId = addObject(
    `<< /Type /Catalog /Pages ${pagesObjectId} 0 R >>`,
  );

  const resolvedObjects = objects.map((objectValue) =>
    objectValue.replace(/PAGES_ID/g, `${pagesObjectId}`),
  );

  let pdf = "%PDF-1.4\n";
  const offsets = [0];

  resolvedObjects.forEach((objectValue, index) => {
    offsets[index + 1] = pdf.length;
    pdf += `${index + 1} 0 obj\n${objectValue}\nendobj\n`;
  });

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${resolvedObjects.length + 1}\n`;
  pdf += "0000000000 65535 f \n";
  offsets.slice(1).forEach((offset) => {
    pdf += `${`${offset}`.padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${resolvedObjects.length + 1} /Root ${catalogObjectId} 0 R >>\n`;
  pdf += `startxref\n${xrefOffset}\n%%EOF`;

  createDownloadLink(
    new Blob([pdf], { type: "application/pdf" }),
    `${APP_NAME}-Budget-${financialYear}.pdf`,
  );
};
